import { useEffect, useState, useCallback } from "react";
import "./deal-details.css";
import Button from "../components/button";
import DemoTutors from "../components/demo-tutors";
import SecondaryButton from "../components/secondary-button";
import { NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Select from "react-select";
import axios from "../axios";
import {
  commonFormTemplate,
  academyFormTemplate,
  curricularFormTemplate,
  competitiveExamsFormTemplate,
  intermediateFormTemplate,
  abroadExamsFormTemplate,
  learnLanguageFormTemplate,
  specialKidsFormTemplate,
  iTCoursesFormTemplate,
  genderConversion,
  modeOfTutionconversion,
} from "../constants/form-templates";
import ConfirmedTutors from "../components/confirmed-tutors";
import LeaveSubscribeModal from "../components/LeaveSubscribeModal";
import axiosInstance from "../axios";

// Custom hook to handle navigation protection
const useNavigationProtection = (
  showPending,
  isSubscribed,
  editRequest,
  showRequest,
  setShowLeaveModal,
  isPaymentProses,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (showPending && !isSubscribed && !isPaymentProses) {
      // Store the original pushState and replaceState methods
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      // Function to check if user has sensitive data that could be lost
      const hasSensitiveData = () => {
        return (
          !window.__ALLOW_LEAVE__ &&
          ((showPending && !isSubscribed) || // Payment pending
            (showRequest && editRequest) || // Form being edited
            showPending || // Any pending state
            showRequest) // Any request state with potential data
        );
      };

      // Function to show appropriate warning - Only use LeaveSubscribeModal for subscribe step
      const showNavigationWarning = () => {
        // Only show LeaveSubscribeModal for subscribe/pending step
        if (showPending && !isSubscribed) {
          setShowLeaveModal(true);
        }
        return false; // Prevent navigation if not confirmed
      };

      // Override pushState to intercept navigation
      window.history.pushState = function (...args) {
        const [state] = args;
        if (hasSensitiveData() && !state?.preventBack && !state?.protectionActive && !window.__ALLOW_LEAVE__) {
          showNavigationWarning();
          return false;
        }
        return originalPushState.apply(window.history, args);
      };

      // Override replaceState to intercept navigation
      window.history.replaceState = function (...args) {
        const [state] = args;
        if (hasSensitiveData() && !state?.preventBack && !state?.protectionActive && !window.__ALLOW_LEAVE__) {
          showNavigationWarning();
          return false;
        }
        return originalReplaceState.apply(window.history, args);
      };

      // Handle popstate (back/forward button) - Chrome optimized approach
      const handlePopState = event => {
        if (hasSensitiveData() && window.__HISTORY_PROTECTION_ACTIVE__) {
          // Stop the navigation immediately
          event.preventDefault();
          event.stopImmediatePropagation();

          // Push current state back to maintain URL
          window.history.pushState({ protectionActive: true }, "", location.pathname);

          // Only show LeaveSubscribeModal for subscribe/pending step
          if (showPending && !isSubscribed) {
            setShowLeaveModal(true);
          }
        }
      };

      window.addEventListener("popstate", handlePopState);

      // Enhanced beforeunload for browser close/refresh
      const handleBeforeUnload = event => {
        if (hasSensitiveData()) {
          // Modern browsers ignore custom messages, but we still need to prevent default
          event.preventDefault();
          // Chrome requires returnValue to be set
          event.returnValue = "";
          // Return empty string as per modern browser requirements
          return "";
        }
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      // Additional Chrome-specific handlers
      const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden" && hasSensitiveData()) {
          // Page is being hidden (potentially closing)
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Handle page unload
      const handleUnload = () => {
        if (hasSensitiveData()) {
          // Last attempt to save data or warn
        }
      };

      window.addEventListener("unload", handleUnload);

      // Cleanup function
      return () => {
        // Restore original methods
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
        window.removeEventListener("popstate", handlePopState);
        window.removeEventListener("beforeunload", handleBeforeUnload);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("unload", handleUnload);
      };
    }
  }, [showPending, isSubscribed, editRequest, showRequest, setShowLeaveModal, location, isPaymentProses]);
};

export default function DealDetails() {
  const [data, setData] = useState({});
  const [showRequest, setShowRequest] = useState(false);
  const [editRequest, setEditRequest] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showAssigned, setShowAssigned] = useState(false);
  const [Form, setForm] = useState();
  const [streamsData, setStreamsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activePayNow, setActivePayNow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [userId] = useState(JSON.parse(localStorage.getItem("userDetails"))?.userId);
  const [userDetails] = useState(JSON.parse(localStorage.getItem("userDetails")));
  const [isSubscribed, setIsSubscribed] = useState(JSON.parse(localStorage.getItem("userDetails"))?.isAuthenticated);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isRequestLoading, setIsRequestLoading] = useState(false);
  const [isNewRequest, setIsNewRequest] = useState(false);
  const [dealData] = useState(JSON.parse(localStorage.getItem("dealData")));
  const [isPaymentProses, setIsPaymentProses] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);

  // Progress tracking states
  const [completedSteps, setCompletedSteps] = useState({
    request: false,
    subscribe: false,
    demo: false,
    assigned: false,
  });

  const reSetAll = useCallback(() => {
    setShowRequest(false);
    setEditRequest(false);
    setShowPending(false);
    setShowDemo(false);
    setShowAssigned(false);
  }, []);

  const _Request = id => {
    if (!["newRequest"].includes(data.status)) {
      reSetAll();
      setShowRequest(true);
    }
  };

  const _Pay = () => {
    if (!["newRequest"].includes(data.status)) {
      reSetAll();
      setShowPending(true);
    }
  };

  const _Demo = () => {
    if (["assigned", "demo"].includes(data.status)) {
      reSetAll();
      setShowDemo(true);
    } else {
      // TODO : Remove else
      reSetAll();
      setShowDemo(true);
    }
  };

  const _Assigned = () => {
    if (["assigned"].includes(data.status)) {
      reSetAll();
      setShowAssigned(true);
    } else {
      // TODO : Remove else
      reSetAll();
      setShowAssigned(true);
    }
  };

  const _Next = () => {
    if (showRequest) {
      // Mark request as completed and move to subscribe
      setCompletedSteps(prev => ({ ...prev, request: true }));
      reSetAll();
      setShowPending(true);
    } else if (showPending) {
      // Mark subscribe as completed and move to demo
      setCompletedSteps(prev => ({ ...prev, subscribe: true }));
      reSetAll();
      setShowDemo(true);
    } else if (showDemo) {
      // Mark demo as completed and move to assigned
      setCompletedSteps(prev => ({ ...prev, demo: true }));
      reSetAll();
      setShowAssigned(true);
    }
  };

  const _Previous = () => {
    if (showAssigned) {
      // Keep demo and subscribe completed when going back from assigned
      reSetAll();
      setShowDemo(true);
    } else if (showDemo) {
      // Keep subscribe completed when going back from demo
      setCompletedSteps(prev => ({ ...prev, demo: false }));
      reSetAll();
      setShowPending(true);
    } else if (showPending) {
      if (isSubscribed) {
        // Keep request completed when going back from subscribe
        setCompletedSteps(prev => ({ ...prev, subscribe: false }));
        reSetAll();
        setShowRequest(true);
      } else if (!isPaymentProses) {
        setShowLeaveModal(true);
      }
    }
  };

  // Check for razorpay_payment_id or payment_failure in URL params and navigate to subscribe tab
  useEffect(() => {
    const razorpayPaymentId = searchParams.get("razorpay_payment_id");
    const paymentFailure = searchParams.get("payment_failure");

    setTimeout(() => {
      if (razorpayPaymentId) {
        // Mark payment as successful and navigate to subscribe tab
        reSetAll();
        setShowPending(true);
        setCompletedSteps(prev => ({
          ...prev,
          request: true,
          subscribe: true,
          demo: false,
          assigned: false,
        }));

        // Update localStorage with subscription status
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        if (userDetails) {
          const updatedUserDetails = { ...userDetails, isAuthenticated: true };
          localStorage.setItem("userDetails", JSON.stringify(updatedUserDetails));
        }

        // Clear the payment ID from URL to prevent repeated execution
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("razorpay_payment_id");
        const newUrl = `${window.location.pathname}${
          newSearchParams.toString() ? "?" + newSearchParams.toString() : ""
        }`;
        window.history.replaceState({}, "", newUrl);
      } else if (paymentFailure || paymentFailed) {
        // Mark payment as failed and navigate to subscribe tab
        reSetAll();
        setShowPending(true);
        setCompletedSteps(prev => ({
          ...prev,
          request: true,
          subscribe: true,
          demo: false,
          assigned: false,
        }));

        setPaymentFailed(true);
        // // Clear the payment_failure from URL to prevent repeated execution
        // const newSearchParams = new URLSearchParams(searchParams);
        // newSearchParams.delete("payment_failure");
        // const newUrl = `${window.location.pathname}${
        //   newSearchParams.toString() ? "?" + newSearchParams.toString() : ""
        // }`;
        // window.history.replaceState({}, "", newUrl);
      }
    }, 200);
  }, []);

  useEffect(() => {
    let latestUserDetails = null;
    try {
      latestUserDetails = JSON.parse(localStorage.getItem("userDetails"));
    } catch (e) {
      latestUserDetails = null;
    }
    if (latestUserDetails && latestUserDetails.userId) {
      axios
        .get(`v1/student/subscription-status?student_id=${latestUserDetails.userId}`)
        .then(data => {
          const response = data.data.data;
          setIsSubscribed(response.status === "Subscribed");
          // Update localStorage with the latest userDetails and isAuthenticated status
          localStorage.removeItem("userDetails");
          const updatedUserDetails = { ...latestUserDetails, isAuthenticated: response.status === "Subscribed" };

          localStorage.setItem("userDetails", JSON.stringify(updatedUserDetails));
        })
        .catch(err => {});
    }
  }, []);

  // Function to handle form submission and mark request as completed
  const handleFormSubmit = formData => {
    // Mark request as completed
    setCompletedSteps(prev => ({ ...prev, request: true }));

    const tution_type = modeOfTutionconversion[formData.find(input => input.inputName === "mode_of_tution").value];
    const tutor_gender_preference = genderConversion[formData.find(input => input.inputName === "tutor_gender").value];
    let newDeal = {
      "dealId": data.id,
      "student_requirments": formData.find(input => input.inputName === "course_name").value.split(","),
      "level":
        data.category_id === 1 || data.category_id === 4
          ? formData.find(input => input.inputName === "course_level").value
          : "Advanced Level",
      tutor_gender_preference,
      "how_many_days_in_a_week": formData.find(input => input.inputName === "no_of_days_per_week_required").value,
      "when_you_like_to_start": formData.find(input => input.inputName === "when_to_start_class").value,
      tution_type,
      "stream_id": formData.find(input => input.inputName === "stream_board").value,
      "category_id": data.category_id,
      "base_module_student_id": data.base_module_student_id,
    };
    axios
      .post(`v1/student/new-deal`, newDeal)
      .then(response => {
        // For UI demo, navigate to next step after successful API call
        reSetAll();
        setShowPending(true);
      })
      .catch(err => {
        // console.log(err);
        // Even if API fails, still navigate for demo purposes
        reSetAll();
        setShowPending(true);
      });
  };

  const postNewDeal = formData => {
    handleFormSubmit(formData);
  };

  const getDealFullDetails = (dealId, Form, streamsData) => {
    axios
      .get(`v1/student/deal-info?dealId=${dealId}`)
      .then(res => {
        res = res.data;
        let restltForm = [];
        let _courseList;
        restltForm = Form.map(itm => {
          switch (itm.inputName) {
            case "stream_board":
              _courseList = streamsData.find(it => it.stream_name?.toLowerCase() === res.stream_id?.toLowerCase());
              itm = { ...itm, "value": res.stream_id };
              break;
            case "course_name":
              itm = { ...itm, "value": res.student_requirments, "options": _courseList.courses };
              break;
            case "tutor_gender":
              itm = { ...itm, "value": genderConversion[res.tutor_gender_preference] };
              break;
            case "no_of_days_per_week_required":
              itm = { ...itm, "value": res.how_many_days_in_a_week };
              break;
            case "course_level":
              itm = { ...itm, "value": res.level };
              break;
            case "when_to_start_class":
              itm = { ...itm, "value": res.when_you_like_to_start };
              break;
            case "mode_of_tution":
              itm = { ...itm, "value": modeOfTutionconversion[res.tution_type] };
              break;
            default:
              itm = { ...itm };
          }
          return itm;
        });
        setForm(restltForm);
        setLoading(false);
      })
      .catch(err => {
        // console.log(err);
        // For demo purposes, still set loading to false
        setLoading(false);
      });
  };

  const fetchCourses = (_dealData, courseLevel) => {
    let dataFromAPI, _streamsList;
    axios
      .get("v1/student/all-courses?category_id=" + _dealData.category_id)
      .then(response => {
        dataFromAPI = response.data;
        setStreamsData(dataFromAPI);
        _streamsList = dataFromAPI.map(itm => itm.stream_name);
        let activeForm;
        switch (_dealData.category_id) {
          case 1:
            activeForm = academyFormTemplate;
            break;
          case 2:
            activeForm = curricularFormTemplate;
            break;
          case 3:
            activeForm = competitiveExamsFormTemplate;
            break;
          case 4:
            activeForm = intermediateFormTemplate;
            break;
          case 5:
            activeForm = abroadExamsFormTemplate;
            break;
          case 6:
            activeForm = learnLanguageFormTemplate;
            break;
          case 7:
            activeForm = iTCoursesFormTemplate;
            break;
          case 13:
            activeForm = specialKidsFormTemplate;
            break;
          default:
            activeForm = academyFormTemplate;
        }
        activeForm = activeForm.map(formInput => {
          if (formInput.inputName === "stream_board") return { ...formInput, "options": _streamsList };
          else if (formInput.inputName === "course_name") return { ...formInput, "options": dataFromAPI[0]?.courses || [] };
          else if (formInput.inputName === "course_level" && courseLevel)
            return { ...formInput, value: courseLevel };
          return formInput;
        });
        activeForm = [...activeForm, ...commonFormTemplate];
        if (_dealData.status !== "newRequest" && !editRequest) getDealFullDetails(_dealData.id, activeForm, dataFromAPI);
        else {
          // If in edit mode or new request, preserve user's current selections in Form
          setForm(prevForm => {
            if (!prevForm || prevForm.length === 0) return activeForm;
            return activeForm.map(newInput => {
              const prevInput = prevForm.find(p => p.inputName === newInput.inputName);
              if (prevInput && newInput.inputName !== "course_level") {
                return { ...newInput, value: prevInput.value };
              }
              return newInput;
            });
          });
          setLoading(false);
        }
      })
      .catch(err => {
        // console.log(err)
        // For demo purposes, set mock data if API fails
        const mockStreamsData = [
          {
            stream_name: "CBSE",
            courses: ["Mathematics", "Science", "English", "Hindi", "Social Studies"],
          },
        ];
        setStreamsData(mockStreamsData);

        let activeForm = academyFormTemplate;
        activeForm = activeForm.map(formInput => {
          if (formInput.inputName === "stream_board")
            return { ...formInput, "options": ["CBSE", "ICSE", "State Board"] };
          else if (formInput.inputName === "course_name")
            return { ...formInput, "options": mockStreamsData[0].courses };
          else if (formInput.inputName === "course_level" && courseLevel !== "")
            return { ...formInput, value: courseLevel };
          return formInput;
        });
        activeForm = [...activeForm, ...commonFormTemplate];
        setForm(activeForm);

        if (_dealData.status !== "newRequest") getDealFullDetails(_dealData.id, activeForm, mockStreamsData);
        else setLoading(false);
      });
    setData(_dealData);

    if (editRequest) return;

    if (_dealData.status?.toLowerCase() === "demo") {
      reSetAll();
      setShowDemo(true);
      // Mark previous steps as completed and reset subsequent steps
      setCompletedSteps(prev => ({
        ...prev,
        request: true,
        subscribe: true,
        assigned: false,
      }));
    } else if (_dealData.status.toLowerCase() === "confirmed") {
      if (showPending && !isSubscribed && !isPaymentProses) {
        setShowLeaveModal(true);
        return;
      }
      reSetAll();
      setShowAssigned(true);
      // Mark all previous steps as completed
      setCompletedSteps(prev => ({
        ...prev,
        request: true,
        subscribe: true,
        demo: true,
      }));
    } else if (_dealData.status.toLowerCase() === "pending") {
      if (showPending && !isSubscribed && !isPaymentProses) {
        setShowLeaveModal(true);
        return;
      }
      reSetAll();
      setShowRequest(true);
      // Reset all subsequent steps when going back to request
      setCompletedSteps(prev => ({
        ...prev,
        subscribe: false,
        demo: false,
        assigned: false,
      }));
    } else if (_dealData.status.toLowerCase() === "payment_pending") {
      reSetAll();
      setShowPending(true);
      setCompletedSteps(prev => ({
        ...prev,
        request: true,
        demo: false,
        assigned: false,
      }));
    } else {
      reSetAll();
      setShowRequest(true);
      setCompletedSteps(prev => ({
        ...prev,
        request: true,
        demo: false,
        assigned: false,
        subscribe: false,
      }));
    }
  };

  const payNow = () => {
    axios
      .post("v1/subscribe-to-plan")
      .then(response => {
        const requestData = response.data;
        // // console.log(requestData.data);
        if (requestData.data.includes("https:")) {
          setTimeout(() => {
            window.open(requestData.data, "_blank");
          });
        }
        // Mark as subscribed for UI
        setIsSubscribed(true);
      })
      .catch(err => {
        // console.log(err);
        // For demo purposes, still mark as subscribed
        setIsSubscribed(true);
      });
  };

  // useEffect(() => {
  //   // For UI demo, also mark as subscribed and auto-navigate
  //   setTimeout(() => {
  //     setIsSubscribed(true);
  //     setCompletedSteps(prev => ({ ...prev, subscribe: true }));
  //     // Auto navigate to next step after payment
  //     setTimeout(() => {
  //       reSetAll();
  //       setShowDemo(true);
  //     }, 1000);
  //   }, 1000);
  // }, [window.location.pathname]);

  // const buySubscription = async planId => {
  //   setIsLoading(true);
  //   setIsPaymentProses(true);
  //   try {
  //     let _dealData = JSON.parse(localStorage.getItem("dealData")) || {
  //       id: "12345",
  //       status: "newRequest",
  //       category_id: 1,
  //       advance_payment: false,
  //     };
  //     const userDetails = JSON.parse(localStorage.getItem("userDetails"));

  //     if (userDetails.specialRequest) {
  //       planId = "plan_ROZ1UKuvncyeXV";
  //     } else {
  //       planId = "plan_RNnGWybdWJBpfx";
  //     }
  //     const payload = {
  //       planId: planId,
  //       userId: JSON.parse(localStorage.getItem("userDetails"))?.userId,
  //     };
  //     await axios
  //       .post(`v1/plans`, payload)
  //       .then(response => {
  //         const data = response.data;
  //         setSubscriptionId(data?.subscriptionId);
  //       })
  //       .catch(err => {
  //         setIsLoading(false);
  //         console.error(err);
  //       });
  //   } catch (error) {
  //     setIsLoading(false);
  //     console.error(error);
  //     // For demo purposes, simulate success even if API fails
  //     setTimeout(() => {
  //       setIsSubscribed(true);
  //       setCompletedSteps(prev => ({ ...prev, subscribe: true }));
  //       setTimeout(() => {
  //         reSetAll();
  //         setShowDemo(true);
  //       }, 1000);
  //     }, 1000);
  //   }
  // };
  const buySubscription = async planId => {
    setIsLoading(true);
    setIsPaymentProses(true);
    try {
      // Step 1: Create order from backend
      const {
        data: { data: order },
      } = await axios.post("v1/student/create-order", {
        planType: data.advance_amount === 1000 ? "Basic" : "Special",
        isStudent: true,
      });

      const user = JSON.parse(localStorage.getItem("userDetails"));

      // Step 2: Configure Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "HomeShiksha",
        description: "HomeHomeShikshash Plan ",
        order_id: order.id,
        callback_url: `${process.env.REACT_APP_DOMAIN_NAME}v1/tutor/order-payment-verification?userId=${
          user.userId
        }&planType=${data.advance_amount === 1000 ? "Basic" : "Special"}&isStudent=true&isDealDetails=true`, // ✅ backend URL
        // prefill: {
        //   name: "HomeShiksha",
        //   email: "homeshiksha@gmail.com",
        //   contact: "9876543210",
        // },
        theme: {
          color: "#2B93A5",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      setLoading(false);
      console.error("Payment Error:", error);
      setIsLoading(false);
      // For demo purposes, simulate success even if API fails
      setTimeout(() => {
        setIsSubscribed(true);
        setCompletedSteps(prev => ({ ...prev, subscribe: true }));
        setTimeout(() => {
          reSetAll();
          setShowDemo(true);
        }, 1000);
      }, 1000);
    }
    // try {
    //   const payload = {
    //     planId: planAmt === 2000 ? "plan_ROZ1UKuvncyeXV" : "plan_RNnGWybdWJBpfx",
    //     userId: JSON.parse(localStorage.getItem("userDetails"))?.userId,
    //   };
    //   const { data } = await axios.post(`v1/plans`, payload);
    //   setSubscriptionId(data?.subscriptionId);
    //   setIsLoading(false);
    // } catch (error) {
    //   setIsLoading(false);
    //   console.error(error);
    // }
  };

  useEffect(() => {
    if (subscriptionId) {
      const openPopUp = () => {
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          name: "HomeShiksha",
          description: "Access to HomeShiksha",
          image: "./profile.jpg",
          subscription_id: subscriptionId,
          callback_url: `${process.env.REACT_APP_DOMAIN_NAME}v1/subscriptions?isDealDetails=true`,
          // prefill: {
          //   name: "HomeShiksha",
          //   email: "homeshiksha@gmail.com",
          //   contact: "9876543210",
          // },
          notes: {
            address: "HomeShiksha",
          },
          theme: {
            color: "#212333",
          },
        };

        const razor = new window.Razorpay(options);
        razor.open();
      };
      openPopUp();
    }
  }, [subscriptionId, userId]);

  const refreshPageData = async () => {
    try {
      let dealId = data.id;

      let response = await axios.get(`v1/student/dashboard`);
      response = response.data?.requests_data?.find(deal => deal.id === dealId);

      switch (response.status) {
        case "newRequest":
          setShowRequest(true);
          setEditRequest(true);
          break;
        case "pending":
          setShowPending(true);
          break;
        case "demo":
          setShowDemo(true);
          break;
        case "assigned":
          setShowAssigned(true);
          break;
        default:
      }

      fetchCourses(response);
      localStorage.setItem("dealData", JSON.stringify(response));
    } catch (error) {
      // For demo purposes, continue with current state
    }
  };

  const loadPageData = () => {
    let _dealData = JSON.parse(localStorage.getItem("dealData")) || {
      id: "12345",
      status: "newRequest",
      category_id: 1,
      advance_payment: false,
    };

    if (_dealData.category_id === 13) {
      _dealData.advance_amount = 2000;
    } else {
      _dealData.advance_amount = 1000;
    }

    // Always select Request as the default step on load
    reSetAll();
    setShowRequest(true);
    const shouldEditBeActive = _dealData.status === "newRequest";
    setEditRequest(shouldEditBeActive);
    setIsNewRequest(shouldEditBeActive);
    switch (_dealData.status) {
      case "pending":
        setShowPending(true);
        setShowRequest(false);
        break;
      case "demo":
        setShowDemo(true);
        setShowRequest(false);
        break;
      case "assigned":
        setShowAssigned(true);
        setShowRequest(false);
        break;
      default:
    }

    fetchCourses(_dealData);
  };

  useEffect(() => {
    refreshPageData();
  }, [activePayNow]);

  useEffect(() => {
    loadPageData();
  }, []);

  // Debug effect to log state changes
  useEffect(() => {
    if (isNewRequest) {
      setEditRequest(true);
    } else {
      setEditRequest(false);
    }
  }, [isNewRequest]);

  useNavigationProtection(showPending, isSubscribed, editRequest, showRequest, setShowLeaveModal, isPaymentProses);

  // Dynamic history state management for back button protection
  useEffect(() => {
    if (!isPaymentProses) {
      const hasSensitiveData =
        !window.__ALLOW_LEAVE__ &&
        ((showPending && !isSubscribed) || (showRequest && editRequest) || showPending || showRequest);

      if (hasSensitiveData && !window.__HISTORY_PROTECTION_ACTIVE__) {
        // Add a dummy history entry to intercept back button
        window.history.pushState({ protectionActive: true }, "", window.location.pathname);
        window.__HISTORY_PROTECTION_ACTIVE__ = true;
      } else if (!hasSensitiveData && window.__HISTORY_PROTECTION_ACTIVE__) {
        // Remove protection when no sensitive data
        window.__HISTORY_PROTECTION_ACTIVE__ = false;
      }
    }
  }, [showPending, isSubscribed, editRequest, showRequest, isPaymentProses]);

  // Handle navigation protection when Subscribe step is active

  // Function to check if a step is completed
  const isStepCompleted = step => {
    return completedSteps[step];
  };

  // Function to check if a step is active
  const isStepActive = step => {
    switch (step) {
      case "request":
        return showRequest;
      case "subscribe":
        return showPending;
      case "demo":
        return showDemo;
      case "assigned":
        return showAssigned;
      default:
        return false;
    }
  };

  // Function to check if a step should be enabled (previous step completed)
  const isStepEnabled = step => {
    switch (step) {
      case "request":
        return true; // Always enabled
      case "subscribe":
        return isStepCompleted("request") || !["newRequest"].includes(data.status);
      case "demo":
        return isStepCompleted("subscribe") || ["demo", "assigned"].includes(data.status) || data.advance_payment;
      case "assigned":
        return isStepCompleted("demo") || ["assigned"].includes(data.status);
      default:
        return false;
    }
  };
  const getDisplayStatus = status => {
    const newRequestStatuses = ["newRequest"];
    const pendingStatuses = ["new", "prospect", "not_interested"];
    const inProgressStatuses = ["active", "inprogress", "assigned", "escalated", "pending"];
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, "_");

    if (pendingStatuses.includes(normalizedStatus)) {
      return "Pending";
    }
    if (inProgressStatuses.includes(normalizedStatus)) {
      return "In-Progress";
    }
    if (newRequestStatuses.includes(normalizedStatus)) {
      return "New Request";
    }
    return status;
  };

  // Function to handle navigation attempts
  const handleNavigationAttempt = (e, targetPath) => {
    // Check if user has sensitive data that could be lost
    if (!isPaymentProses) {
      const hasSensitiveData =
        !window.__ALLOW_LEAVE__ &&
        ((showPending && !isSubscribed) || // Payment pending
          (showRequest && editRequest) || // Form being edited
          showPending || // Any pending state
          showRequest); // Any request state with potential data

      if (hasSensitiveData) {
        e.preventDefault();

        // Only use LeaveSubscribeModal for subscribe/pending step
        if (showPending && !isSubscribed && !isPaymentProses) {
          setShowLeaveModal(true);
        }
        return false;
      }
    }
    return true;
  };

  // Function to handle leave modal submission
  const handleLeaveModalSubmit = reason => {
    setIsRequestLoading(true);
    axiosInstance
      .post(`/v1/close-request`, {
        userId: userId,
        request_id: 1,
        request_value: reason,
        custom_text: reason,
      })
      .then(data => {
        // Allow navigation just for this confirmed action
        window.__ALLOW_LEAVE__ = true;
        setIsRequestLoading(false);
        setShowLeaveModal(false);
        // Navigate to dashboard after user confirms leaving
        setTimeout(() => {
          navigate("/dashboard");
          // reset the flag shortly after navigation
          setTimeout(() => {
            window.__ALLOW_LEAVE__ = false;
          }, 500);
        }, 0);
      });
  };

  return !loading ? (
    <main className="main">
      <section className="pathway-requestBtn--wrapper">
        <div className="pathway--wrapper">
          <div>
            <NavLink to="/dashboard">
              <img src="/homeicon.svg" alt="homeicon icon" width="18" height="17" />
            </NavLink>
          </div>
          <div>
            {" "}
            <img src="/rightArrow.svg" alt="rightArrow icon" width="11" height="11" />
          </div>
          <div className="pathway--btn">
            <NavLink to="/dashboard">All Requests</NavLink>
          </div>
          <div>
            <img src="/rightArrow.svg" alt="rightArrow icon" width="11" height="11" />
          </div>
          <div className="pathway--btn"> Request {data.id}</div>
        </div>
        <div className="request-btn--wrapper">
          <NavLink to="/new-request" onClick={e => handleNavigationAttempt(e, "/new-request")}>
            <Button>New Request</Button>
          </NavLink>
        </div>
      </section>

      <section className="deal-heading--wrapper">
        <div className="status--wrapper">
          <div className="deal-heading--subheading">Current Status : </div>
          <div className="deal-heading--value">{getDisplayStatus(data.status)}</div>
        </div>
        <div className="requestId--wrapper">
          <div className="deal-heading--subheading">Request ID : </div>
          <div className="deal-heading--subheading">{data.id}</div>
        </div>
      </section>

      <section className="deal-info--container">
        <div className="deal-info--container--title-wrapper">
          <div className="deal-info--container--title">Request Title</div>
          <div className="deal-info--container--editOption">
            {!data.advance_payment && !editRequest && (
              <div
                onClick={() => {
                  reSetAll();
                  setEditRequest(() => true);
                  setShowRequest(() => true);
                }}>
                Edit Request
                <img src="/pencil-icon.svg" alt="pencil icon" width="15" height="15" />
              </div>
            )}
          </div>
        </div>

        <div className="dealsFlow-Status--container">
          <div className="dealFlow-icons--wrapper">
            <div className="deal-mileStone--wrapper">
              <div className="deal-mileStone--icon--wrapper" style={{ backgroundColor: "rgba(77, 161, 188, 0.4)" }}>
                <div className="deal-mileStone--icon">
                  <img src="/request-icon.svg" alt="request icon" width="30" height="30" />
                </div>
              </div>
              <div
                className="deal-mileStone--status--wrapper"
                style={{
                  backgroundColor: `${isStepCompleted("request") ? "#2B93A5" : "white"}`,
                }}>
                {isStepCompleted("request") ? (
                  <img src="/tick-icon.svg" alt="tick icon" width="14" height="12" />
                ) : (
                  <span style={{ color: "#2B93A5", fontWeight: 900 }}>{showRequest ? "i" : ""}</span>
                )}
              </div>
              <div
                className="deal-mileStone--title"
                onClick={() => {
                  if (isStepEnabled("request")) {
                    if (showPending && !isSubscribed && !isPaymentProses) {
                      setShowLeaveModal(true);
                      return;
                    }
                    reSetAll();
                    setShowRequest(true);
                    // Reset all subsequent steps when going back to request
                    setCompletedSteps(prev => ({
                      ...prev,
                      subscribe: false,
                      demo: false,
                      assigned: false,
                    }));
                  }
                }}
                style={{
                  backgroundColor: showRequest || showPending || showDemo || showAssigned ? "#2B93A5" : "white",
                  color: showRequest || showPending || showDemo || showAssigned ? "white" : "black",
                  cursor: isStepEnabled("request") ? "pointer" : "default",
                }}>
                Request
              </div>
            </div>

            <div className="deal-mileStone--wrapper">
              <div className="deal-mileStone--icon--wrapper" style={{ backgroundColor: "rgba(188, 157, 77, 0.4)" }}>
                <div className="deal-mileStone--icon">
                  <img src="/payment-icon.svg" alt="payment icon" width="30" height="30" />
                </div>
              </div>
              <div
                className="deal-mileStone--status--wrapper"
                style={{
                  backgroundColor: `${isStepCompleted("subscribe") ? "#2B93A5" : "white"}`,
                }}>
                {isStepCompleted("subscribe") ? (
                  <img src="/tick-icon.svg" alt="tick icon" width="14" height="12" />
                ) : (
                  <span style={{ color: "#2B93A5", fontWeight: 900 }}>{showPending ? "i" : ""}</span>
                )}
              </div>
              <div
                className="deal-mileStone--title"
                style={{
                  backgroundColor: isStepCompleted("subscribe") || showPending ? "#2B93A5" : "white",
                  color: isStepCompleted("subscribe") || showPending ? "white" : "black",
                  cursor: isStepEnabled("subscribe") ? "pointer" : "default",
                }}
                onClick={() => {
                  if (isStepEnabled("subscribe")) {
                    reSetAll();
                    setShowPending(true);
                    // Mark request as completed and reset subsequent steps
                    setCompletedSteps(prev => ({
                      ...prev,
                      request: true,
                      demo: false,
                      assigned: false,
                    }));
                  }
                }}>
                {isSubscribed ? "Subscribed" : "Subscribe"}
              </div>
            </div>

            <div className="deal-mileStone--wrapper">
              <div className="deal-mileStone--icon--wrapper" style={{ backgroundColor: "rgba(188, 77, 117, 0.4)" }}>
                <div className="deal-mileStone--icon">
                  <img src="/demo-icon.svg" alt="demo icon" width="30" height="30" />
                </div>
              </div>

              <div
                className="deal-mileStone--status--wrapper"
                style={{
                  backgroundColor: `${isStepCompleted("demo") ? "#2B93A5" : "white"}`,
                }}>
                {isStepCompleted("demo") ? (
                  <img src="/tick-icon.svg" alt="tick icon" width="14" height="12" />
                ) : (
                  <span style={{ color: "#2B93A5", fontWeight: 900 }}>{showDemo ? "i" : ""}</span>
                )}
              </div>
              <div
                className="deal-mileStone--title"
                style={{
                  backgroundColor: isStepCompleted("demo") || showDemo ? "#2B93A5" : "white",
                  color: isStepCompleted("demo") || showDemo ? "white" : "black",
                  cursor: isStepEnabled("demo") ? "pointer" : "default",
                }}
                onClick={() => {
                  if (isStepEnabled("demo")) {
                    reSetAll();
                    setShowDemo(true);
                    // Mark previous steps as completed and reset subsequent steps
                    setCompletedSteps(prev => ({
                      ...prev,
                      request: true,
                      subscribe: true,
                      assigned: false,
                    }));
                  }
                }}>
                Demo
              </div>
            </div>

            <div className="deal-mileStone--wrapper">
              <div className="deal-mileStone--icon--wrapper" style={{ backgroundColor: "rgba(132, 188, 77, 0.4)" }}>
                <div className="deal-mileStone--icon">
                  <img src="/assigned-icon.svg" alt="assigned icon" width="30" height="30" />
                </div>
              </div>
              <div
                className="deal-mileStone--status--wrapper"
                style={{
                  backgroundColor: `${isStepCompleted("assigned") ? "#2B93A5" : "white"}`,
                }}>
                {isStepCompleted("assigned") ? (
                  <img src="/tick-icon.svg" alt="tick icon" width="14" height="12" />
                ) : (
                  <span style={{ color: "#2B93A5", fontWeight: 900 }}>{showAssigned ? "i" : ""}</span>
                )}
              </div>
              <div
                className="deal-mileStone--title "
                style={{
                  backgroundColor: isStepCompleted("assigned") || showAssigned ? "#2B93A5" : "white",
                  color: isStepCompleted("assigned") || showAssigned ? "white" : "black",
                  cursor: isStepEnabled("assigned") ? "pointer" : "default",
                }}
                onClick={() => {
                  if (isStepEnabled("assigned")) {
                    if (showPending && !isSubscribed && !isPaymentProses) {
                      setShowLeaveModal(true);
                      return;
                    }
                    reSetAll();
                    setShowAssigned(true);
                    // Mark all previous steps as completed
                    setCompletedSteps(prev => ({
                      ...prev,
                      request: true,
                      subscribe: true,
                      demo: true,
                    }));
                  }
                }}>
                Confirmed
              </div>
            </div>
          </div>

          <div className="dealFlow-lines-wrapper">
            <div className="dealFlow-solid-line"></div>
            <div className="dealFlow-dotted-line"></div>
          </div>
        </div>

        {!showAssigned && <div className="end-of-dealFlow-icons-line"></div>}
        {showPending && (
          <div style={{ padding: "20px" }}>
            {isSubscribed ? (
              <>
                <div
                  className="payment-pending--wrapper"
                  style={{
                    padding: "30px 20px",
                    backgroundColor: "white",
                    border: "1px solid #E0E0E0",
                    borderRadius: "16px",
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                    textAlign: "center",
                    height: "auto",
                  }}>
                  <div
                    style={{
                      color: "#2B93A5",
                      fontSize: "24px",
                      marginBottom: "20px",
                      fontWeight: "600",
                    }}>
                    Congratulations! You are Already Upgraded to
                    <br />
                    HOMESHIKSHA PREFERRED CUSTOMER
                  </div>
                  <div style={{ margin: "20px auto" }}>
                    <svg width="89" height="68" viewBox="0 0 89 68" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5.5 41.9632L21.88 59.996C22.4327 60.6109 23.141 61.1108 23.9515 61.4581C24.762 61.8049 25.6539 61.9904 26.56 61.9996C27.4515 62.0089 28.3342 61.847 29.1438 61.5275C29.9533 61.2074 30.6694 60.7368 31.24 60.1501L83.5 6"
                        stroke="#05A315"
                        strokeWidth="11"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "600",
                      color: "#05A315",
                      marginBottom: "15px",
                    }}>
                    Payment Done
                  </div>
                  <div style={{ fontSize: "18px", color: "#555", lineHeight: "1.5" }}>
                    Respective CRE has been assigned.
                    <br />
                    Invite tutors for Demo and select of your choice
                  </div>
                  {/* <div style={{ marginTop: "20px" }}>
                    <button
                      className="primary-btn"
                      onClick={() => {
                        reSetAll();
                        setShowDemo(true);
                      }}
                      style={{ cursor: "pointer" }}>
                      Continue to Demo
                    </button>
                  </div> */}
                </div>
              </>
            ) : paymentFailed ? (
              <div
                className="payment-failed--wrapper"
                style={{
                  padding: "30px 20px",
                  backgroundColor: "white",
                  border: "1px solid #E74C3C",
                  borderRadius: "16px",
                  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                  textAlign: "center",
                  height: "auto",
                }}>
                <div
                  style={{
                    color: "#E74C3C",
                    fontSize: "24px",
                    marginBottom: "20px",
                    fontWeight: "600",
                  }}>
                  Payment Failed
                </div>
                <div style={{ margin: "20px auto" }}>
                  <svg width="89" height="68" viewBox="0 0 89 68" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M20 20L68 68M68 20L20 68"
                      stroke="#E74C3C"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    color: "#555",
                    lineHeight: "1.5",
                    marginBottom: "20px",
                  }}>
                  Your payment could not be processed. Please try again or contact support if the issue persists.
                </div>
                <div style={{ marginTop: "20px" }}>
                  <button
                    className="primary-btn"
                    onClick={() => {
                      setPaymentFailed(false);
                      buySubscription("plan_RNnGWybdWJBpfx");
                    }}
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#2B93A5",
                      color: "white",
                      padding: "12px 30px",
                      borderRadius: "8px",
                      fontWeight: "600",
                      border: "none",
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                    }}>
                    Try Again
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="pending--wrapper"
                style={{
                  border: "1px solid #2B93A5",
                  borderRadius: "16px",
                  overflow: "hidden",
                }}>
                {
                  <div className="payment-pending--wrapper" style={{ padding: "30px 20px", backgroundColor: "white" }}>
                    <div
                      className="payment-pending--title"
                      style={{ color: "#2B93A5", fontSize: "24px", marginBottom: "15px" }}>
                      Upgrade to HOMESHIKSHA PREFERRED CUSTOMER
                    </div>
                    <div
                      className="payment-pending--description"
                      style={{
                        color: "#333",
                        fontWeight: "400",
                        fontSize: "16px",
                        lineHeight: "1.5",
                        marginBottom: "20px",
                        textAlign: "center",
                      }}>
                      *We get over 500 requirements for tutors daily. Sometimes its not possible for us to arrange
                      tutors for everyone. If you genuinely have a requirement for a tutor, then pay this small
                      'refundable' fee of Rs.{data.advance_amount} and your requirement will be given first priority.
                    </div>
                    {!activePayNow ? (
                      <div
                        className="payment-pending--paynow-btn"
                        style={{
                          margin: "15px auto",
                          width: "180px",
                        }}
                        onClick={() => {
                          buySubscription("plan_RNnGWybdWJBpfx");
                        }}>
                        <div
                          style={{
                            backgroundColor: "#2B93A5",
                            color: "white",
                            padding: "12px 30px",
                            borderRadius: "8px",
                            fontWeight: "600",
                            cursor: "pointer",
                            textAlign: "center",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                          }}>
                          {isLoading ? "Loading..." : "Pay now"}
                        </div>
                        {/* To reflect Your Payment transaction */}
                      </div>
                    ) : (
                      <div
                        className="payment-pending--paynow-btn"
                        style={{
                          margin: "15px auto",
                          width: "180px",
                        }}
                        onClick={() => {
                          setActivePayNow(false);
                        }}>
                        <div
                          style={{
                            backgroundColor: "#2B93A5",
                            color: "white",
                            padding: "12px 30px",
                            borderRadius: "8px",
                            fontWeight: "600",
                            cursor: "pointer",
                            textAlign: "center",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                          }}>
                          Click here
                        </div>
                        <div style={{ textAlign: "center", marginTop: "5px", fontSize: "14px" }}>
                          To reflect Your Payment transaction
                        </div>
                      </div>
                    )}
                    <div
                      className="payment-pending--payment-amount"
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#333",
                        marginTop: "10px",
                      }}>
                      Advance Payment {data.advance_amount ?? "1000"}/-
                    </div>
                  </div>
                }
                <div className="pending--process-wrapper">
                  <div
                    className="inner-pending--process-wrapper"
                    style={{
                      backgroundColor: "#EDFCFF",
                      padding: "25px 30px",
                      borderRadius: "16px",
                    }}>
                    <div
                      className="pending--process--title"
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#333",
                        marginBottom: "15px",
                      }}>
                      Process:
                    </div>
                    <div className="pending--process--steps--wrapper">
                      <div style={{ marginBottom: "20px" }}>
                        {[1, 2, 3, 4, 5].map((num, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              marginBottom: "12px",
                            }}>
                            <div style={{ color: "#2B93A5", marginRight: "10px", fontWeight: "600" }}>▶</div>
                            <div style={{ color: "#333", fontSize: "16px", lineHeight: "1.5" }}>
                              {num === 1 && "Team will contact you within 1-2 working hour."}
                              {num === 2 && "Your requirement will be put on first priority."}
                              {num === 3 &&
                                "Dedicated Customer Relationship manager will be assigned to handle your requirement."}
                              {num === 4 &&
                                "This amount is fully refundable in case we are not able to Provide you the tutor as per your need."}
                              {num === 5 &&
                                `You will become our subscribed customer for ${
                                  userDetails.specialRequest && data.advance_amount === 2000 ? "12" : "6"
                                } months. You will can avail free tutor replacements and have the option to request an unlimited number of tutors during this period.`}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="pending--payment--TC"
                  style={{
                    textAlign: "center",
                    padding: "15px 0",
                    borderTop: "1px solid #E0E0E0",
                    fontSize: "14px",
                    color: "#555",
                  }}>
                  ★ This amount is fully refundable in case we are not able to Provide you the tutor as per your need ★
                  <div style={{ marginTop: "5px", fontSize: "12px", color: "#777" }}>Terms and conditions applied*</div>
                </div>
              </div>
            )}
          </div>
        )}

        {showDemo && (
          <div>
            <DemoTutors
              showText={false}
              showPending={showPending}
              isSubscribed={isSubscribed}
              setShowLeaveModal={setShowLeaveModal}
            />
            {/* <div style={{ padding: '20px', textAlign: 'center' }}>
              <Button
                onClick={() => {
                  setCompletedSteps(prev => ({ ...prev, demo: true }));
                  reSetAll();
                  setShowAssigned(true);
                }}>
                Complete Demo & Assign Tutor
              </Button>
            </div> */}
          </div>
        )}
        {showAssigned && (
          <div className="assigned-tutors--container">
            {/* <TutorCard data={{ 'status': 'assigned' }} />
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Button onClick={() => {
                setCompletedSteps(prev => ({ ...prev, assigned: true }));
                alert('Congratulations! Your tutor has been successfully assigned.');
              }}>
                Complete Assignment
              </Button>
            </div> */}
            <ConfirmedTutors />
          </div>
        )}
        {showRequest && (
          <div className="showRequest--container">
            <div className="showRequest--title-wrapper">
              <div className="showRequest--title">Request Details</div>
              {!data.advance_payment && !editRequest && (
                <div
                  className="showRequest--Edit-btn"
                  onClick={() => {
                    reSetAll();
                    setEditRequest(() => true);
                    setShowRequest(() => true);
                  }}>
                  {" "}
                  Edit <img src="/pencil-icon.svg" alt="pencil icon" width="15" height="15" />
                </div>
              )}
            </div>

            <form
              onSubmit={e => {
                e.preventDefault();
                reSetAll();
                setShowRequest(() => true);
                postNewDeal(Form);
              }}
              className="showRequest-inputs-wrapper">
              <div className="small-inputs--wrapper">
                {Form.map(input => {
                  let formInputField;
                  switch (input.inputType) {
                    case "dropDown-select":
                      let coursesList = input.options;
                      if (input.inputName === "course_name" && Form[0].inputName === "course_level") {
                        if (["Primary / Pre-Primary", "LKG / UKG"].includes(Form[0].value))
                          coursesList = ["All Subjects"];
                        else if (
                          [
                            "1st Class",
                            "2nd Class",
                            "3rd Class",
                            "4th Class",
                            "5th Class",
                            "6th Class",
                            "7th Class",
                          ].includes(Form[0].value)
                        ) {
                          coursesList = coursesList.filter(
                            course =>
                              ![
                                "All Subjects",
                                "All Subjects Except Language",
                                "Physics",
                                "Chemistry",
                                "Biology",
                                "History",
                                "Geography",
                                "Political Science",
                              ].includes(course),
                          );
                          coursesList = ["All Subjects", "All Subjects Except Language", ...coursesList];
                        } else if (["8th Class", "9th Class", "10th Class"].includes(Form[0].value))
                          coursesList = coursesList.filter(
                            course => !["All Subjects", "All Subjects Except Language"].includes(course),
                          );
                        else if (["11th Class", "12th Class"].includes(Form[0].value))
                          coursesList = coursesList.filter(course => !["Science", "Accountancy"].includes(course));
                      }
                      formInputField = (
                        <div className="small-input--wrapper">
                          <label>{input.title}</label>
                          <div className="input-icon">
                            <div className="blue-dot-inside-input"></div>
                            <Select
                              required
                              options={coursesList.map(otp => ({
                                label: otp,
                                isDisabled: !editRequest,
                                value: otp.split(" ").join("_"),
                              }))}
                              isMulti
                              onChange={val => {
                                if (!editRequest) return;
                                let allCourses = "";
                                val.map(course => (allCourses += course.label + ","));
                                setForm(pre =>
                                  pre.map(itm => {
                                    if (itm.inputName === "course_name")
                                      itm.value = allCourses.substring(0, allCourses.length - 1);
                                    return itm;
                                  }),
                                );
                              }}
                              value={input.value.split(",").map(
                                opt =>
                                  opt.length >= 1 && {
                                    label: opt,
                                    value: opt.split(" ").join("_"),
                                  },
                              )}
                            />
                          </div>
                        </div>
                      );
                      break;
                    case "dropDown":
                      formInputField = (
                        <div className="small-input--wrapper">
                          <label>{input.title}</label>
                          <div className="input-icon">
                            <div className="blue-dot-inside-input"></div>
                            <select
                              name={input.inputName}
                              disabled={!editRequest}
                              required
                              value={input.value}
                              onChange={e => {
                                // if(e.target.value==="")alert("Invalid Selection")
                                setForm(pre =>
                                  pre.map(itm => {
                                    if (itm.inputName === e.target.name) itm.value = e.target.value;
                                    return itm;
                                  }),
                                );
                                if (e.target.name === "stream_board") {
                                  let _courseList = streamsData.find(
                                    dataField => dataField.stream_name === e.target.value,
                                  );
                                  setForm(pre =>
                                    pre.map(itm => {
                                      return itm.inputName === "course_name"
                                        ? {
                                            ...itm,
                                            "options": _courseList.courses,
                                            value: "",
                                          }
                                        : itm;
                                    }),
                                  );
                                }
                                if (
                                  e.target.name === "course_level" &&
                                  [
                                    "Primary / Pre-Primary",
                                    "LKG / UKG",
                                    "1st Class",
                                    "2nd Class",
                                    "3rd Class",
                                    "4th Class",
                                    "5th Class",
                                    "6th Class",
                                    "7th Class",
                                    "8th Class",
                                    "9th Class",
                                    "10th Class",
                                    "11th Class",
                                    "12th Class",
                                  ].includes(e.target.value)
                                ) {
                                  if (["11th Class", "12th Class"].includes(e.target.value) && data.category_id !== 4)
                                    fetchCourses({ ...data, category_id: 4 }, e.target.value);
                                  if (
                                    [
                                      "Primary / Pre-Primary",
                                      "LKG / UKG",
                                      "1st Class",
                                      "2nd Class",
                                      "3rd Class",
                                      "4th Class",
                                      "5th Class",
                                      "6th Class",
                                      "7th Class",
                                      "8th Class",
                                      "9th Class",
                                      "10th Class",
                                    ].includes(e.target.value) &&
                                    data.category_id !== 1
                                  )
                                    fetchCourses({ ...data, category_id: 1 }, e.target.value);
                                }
                              }}>
                              <option value="" hidden>
                                {" "}
                                Select Value{" "}
                              </option>
                              {input.options.map(opt => (
                                <option value={opt}> {opt} </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                      break;
                    default:
                      formInputField = (
                        <div className="small-input--wrapper">
                          <label>{input.title}</label>
                          <div className="input-icon">
                            <div className="blue-dot-inside-input"></div>
                            <input
                              type="text"
                              name={input.inputName}
                              readOnly={!editRequest}
                              onChange={e =>
                                setForm(pre =>
                                  pre.map(itm => {
                                    if (itm.inputName === e.target.name) itm.value = e.target.value;
                                    return itm;
                                  }),
                                )
                              }
                              value={input.value}
                            />
                          </div>
                        </div>
                      );
                  }
                  return formInputField;
                })}
              </div>

              {editRequest && (
                <div className="showRequest-Save-wrapper">
                  <div className="showRequest-cancel-wrapper">
                    <button
                      className="secondary-btn"
                      onClick={() => {
                        // Use browser confirm for form cancel, not LeaveSubscribeModal
                        const confirmed = window.confirm("Are you sure you want to cancel? Your changes will be lost.");
                        if (confirmed) {
                          setEditRequest(false);
                        }
                      }}
                      type="button">
                      Cancel
                    </button>
                  </div>
                  <button className="primary-btn" type="submit">
                    Save
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </section>

      <section className="dealFlow-footer-btn--wrapper">
        {!showRequest ? (
          <span onClick={() => _Previous()}>
            <SecondaryButton>Previous</SecondaryButton>
          </span>
        ) : (
          <></>
        )}
        {!showAssigned ? (
          <button onClick={() => _Next()} disabled={editRequest} className="all-unset-btn">
            <Button>Next</Button>
          </button>
        ) : (
          <></>
        )}
      </section>

      {/* <ConfirmationModal
        open={confirmationModal.open}
        onClose={hideConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
        type={confirmationModal.type}
      /> */}

      <LeaveSubscribeModal
        open={showLeaveModal}
        isLoading={isRequestLoading}
        onClose={() => setShowLeaveModal(false)}
        onSubmit={reason => {
          handleLeaveModalSubmit(reason);
        }}
      />
    </main>
  ) : (
    <main className="main" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
      {" "}
      <div className="loading">
        {" "}
        <img src="./book-loader.gif" alt=" Loading . . .  " />
      </div>
    </main>
  );
}
